import {computed, inject, Injectable} from '@angular/core';
import {TaAppStore} from '../state/clcta-store';
import {
  BasicTaDetails,
  FullTaDetails,
  FullUserDetails,
  PermissionScope,
  TravellerDetails, UserGroupDetails,
  UserPermissionSummaryPermissions
} from '../api';
import {
  ApproverType,
  CANCEL_FLOW_STATUSES,
  CLOSED_TA_STATUSES,
  PREPAYMENT_CANCELLABLE_STATUSES,
  toApproverType
} from '../util/app.types';
import {currentPendingApproval} from '../util/approval-workflow.utils';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private static readonly DEFAULT_BLOCKED_MESSAGE =
    'Your account has been blocked by an administrator. Please contact your manager.';

  store = inject(TaAppStore);

  currentTa = computed<FullTaDetails | undefined>(() => this.store.taSubmission())
  currentUser = computed<FullUserDetails | undefined>(() => this.store.session().details?.user);
  currentPermissions = computed<UserPermissionSummaryPermissions | null | undefined>(() => {
    return this.store.session().details?.user?.permissionSummary?.permissions
  });
  currentGroups = computed<UserGroupDetails[]>(() => {
    // console.log('console.log',this.store.session().details?.user?.permissionSummary?.groups ?? [])
    return this.store.session().details?.user?.permissionSummary?.groups ?? [];
  });
  canCreateTa = computed<boolean>(() => !!this.currentUser()?.isActive);

  // The single approver relationship the server resolved for this session - see ApproverType. Drives
  // which tabs the approvals and manager dashboards offer, nothing else.
  approverType = computed<ApproverType | undefined>(() =>
    toApproverType(this.currentUser()?.approverType)
  );

  // Managing a unit is a data relationship, not a permission, so this is read straight off the
  // session. Deliberately not the same test as approverType === 'unit', which also covers a unit
  // approver who manages no unit.
  managedUnitCount = computed<number>(() => this.currentUser()?.managedUnits?.length ?? 0);

  // A missing user means the session hasn't resolved yet, not that the account is blocked -
  // warning before we know would flash on every initial load. The real gates (canCreateTa,
  // canSubmit) are independently false while the user is null, so nothing is exposed.
  accountBlocked = computed<boolean>(() => {
    const user = this.currentUser();
    return !!user && !user.isEnabled;
  });
  blockedMessage = computed<string>(() =>
    this.currentUser()?.blockedMessage ?? PermissionService.DEFAULT_BLOCKED_MESSAGE
  );

  ////// General Permissions Functions
  isSystemAdmin(): boolean {
    return this.currentGroups().filter(s => s.code === 'systemAdmins').length > 0;
  }

  hasPermission(action: keyof UserPermissionSummaryPermissions, anyScope: PermissionScope[]): boolean {
    if (!this.currentPermissions()) {
      return false;
    }
    if (this.isSystemAdmin()) {
      return true;
    }
    const permissionScope = this.currentPermissions()![action] ?? []
    return permissionScope.some(scope => anyScope.includes(scope));
  }

  hasGroupCodes(codes: string[]): boolean {
    if (!this.currentGroups()) {
      return false;
    }
    if (this.isSystemAdmin()) {
      return true;
    }
    return this.currentGroups().some((group) => codes.filter(code => code === group.code).length > 0);
  }

  ////// Action Permissions
  //The primary traveller is able to edit all parts/steps of the TA.
  canEditTaPrimary(taSubmission?: FullTaDetails | BasicTaDetails): boolean {
    return this.isSystemAdmin()
      || this.hasGroupCodes(['financeAdmins','travelOfficers'])
      || this.currentUser()?.id === (taSubmission?.primaryTraveller?.details?.id ?? this.currentTa()?.primaryTraveller?.details?.id)
      // canBeDelegateForTa returns false for undefined, so fall back to the current TA - most
      // call sites pass no argument and would otherwise never reach the delegate branch.
      || this.canBeDelegateForTa(taSubmission ?? this.currentTa());
  }

  // Primary and secondary travelers can edit the itinerary step (or part of it), and Finance Admins or Travel Officers.
  canEditItinerary(): boolean {
    return this.isSystemAdmin() || this.currentUser()?.id === this.currentTa()?.primaryTraveller?.details?.id ||
      (this.currentTa()?.travellers?.map(t => t.details?.id).includes(this.currentUser()?.id) ?? false)
      || this.canBeDelegateForTa(this.currentTa()) // booking officer edits the itinerary as the primary traveller would
      || this.hasPermission('ManageTaSubmission', [PermissionScope.Global]) // Finance Admin has this, travel officer doesn't
      || this.hasPermission('ManualOverride', [PermissionScope.Global]); // for travel officer
  }

  //Primary traveller can edit any traveller itinerary, secondary travellers can edit their own itinerary.
  canEditTravellerItinerary(traveller: TravellerDetails): boolean {
    return this.isSystemAdmin() || this.currentUser()?.id === this.currentTa()?.primaryTraveller?.details?.id || this.currentUser()?.id === traveller?.details?.id
      || this.canBeDelegateForTa(this.currentTa()) // booking officer may edit every traveller's itinerary
      || this.hasPermission('ManageTaSubmission', [PermissionScope.Global]) // Finance Admin has this, travel officer doesn't
      || this.hasPermission('ManualOverride', [PermissionScope.Global]); // for travel officer;
  }

  canOverrideItinerary(): boolean {
    return this.hasPermission('ManualOverride', [PermissionScope.Global]);
  }

  canUploadDocuments(): boolean {
    return this.hasPermission('ManageDocuments', [PermissionScope.Global]) ||
      (this.hasPermission('ManageDocuments', [PermissionScope.Owner]) && this.currentUser()?.id === this.currentTa()?.primaryTraveller?.details?.id) ||
      (this.hasPermission('ManageDocuments', [PermissionScope.Assigned])) //Server will only let the people who have had this TA assigned to THEM access it -> no secondary check needed
  }

  //canViewDocuments unused -> always allowed

  // A closed TA is read-only for everyone. The elevated rights below are for correcting a TA that
  // is still in flight, not for reopening one that has finished.
  isTaClosed(): boolean {
    const status = this.currentTa()?.status;
    return !!status && CLOSED_TA_STATUSES.includes(status);
  }

  // Deleted sits alongside the closed statuses rather than inside them - canCancel below and
  // taCommentCapabilitiesFor pair the two the same way.
  canEditTaAfterApproval(): boolean {
    return !this.isTaClosed() && this.currentTa()?.status !== 'Deleted'
      && this.hasGroupCodes(['systemAdmins', 'financeAdmins', 'travelOfficers'])
  }

  canEditTaBeforeApproval(): boolean {
    return this.hasGroupCodes(['systemAdmins', 'financeAdmins','travelOfficers'])
  }

  // Quick Search (Ctrl+K / ⌘K) is limited to finance, travel officers and system admins.
  canQuickSearch(): boolean {
    return this.hasGroupCodes(['systemAdmins', 'financeAdmins', 'travelOfficers']);
  }

  canSubmit(): boolean {
    if (!this.currentTa()) {
      return false;
    }

    if (this.currentUser() == null){
      return false;
    }

    if (this.isSystemAdmin()) {
      return true;
    }

    if (!this.currentUser()!.isEnabled! || !this.currentUser()?.isActive!){
      return false;
    }

    // The booking officer may submit the TA they raised - the server allows it too. The
    // createdById check below covers the common case, but the delegate can be reassigned.
    if (this.canBeDelegateForTa(this.currentTa())) {
      return true;
    }

    return !(this.currentUser()?.id !== this.store.taSubmission()?.created?.createdById && this.currentUser()?.id !== this.store.taSubmission()?.primaryTraveller?.details?.id);
  }

  canCancel(): boolean {
    if (!this.currentTa()) {
      return false;
    }
    if (this.isTaClosed() || this.currentTa()?.status === 'Deleted') {
      return false;
    }
    // A cancellation is already under way - the server refuses a second cancel request on either
    // of these (SubmissionController.CancelTaSubmission, TAStatus.IsCancelFlow).
    if (this.currentTa()?.status === 'PendingCancelApproval' || this.currentTa()?.status === 'CancelApproved') {
      return false;
    }
    // Global scope (Finance Admin, Sys Admin) can cancel any TA
    if (this.hasPermission('ManageTaSubmission', [PermissionScope.Global])) {
      return true;
    }
    // Booking officer may cancel the TA they raised, even before a primary traveller is set
    if (this.canBeDelegateForTa(this.currentTa())) {
      return true;
    }
    // Primary traveller can cancel their own TA
    return !!this.currentUser()?.id && this.currentTa()!.primaryTraveller?.details?.id === this.currentUser()?.id;
  }

  //canViewTaValue unused -> always allowed

  //canViewAuditLog unused -> always allowed

  // Based on permissions from user group membership or if they are a unit manager. The server
  // restricts unit managers to TAs raised against their managed units' project codes.
  canViewSearch(): boolean {
    return this.hasPermission('ViewTaSubmission', [PermissionScope.Global]) || this.managedUnitCount() > 0;
  }

  // only if they have global search permission based on group membership. nothing to do with managed units.
  hasGlobalSearch(): boolean {
    return this.hasPermission('ViewTaSubmission', [PermissionScope.Global]);
  }

  canApproveAsGroupMember(taSubmission?: FullTaDetails | undefined): boolean {
    const ta = taSubmission ?? this.currentTa();
    if (!ta) return false;
    if (this.isSystemAdmin()) {
      return true;
    }
    const userGroupCodes = new Set(this.currentGroups().map(g => g.code).filter((c): c is string => !!c));
    if (userGroupCodes.size === 0) return false;
    return (ta.approvals ?? []).some(
      a => a.status !== 'Actioned' && a.groupApprovers?.some(ga => ga.code != null && userGroupCodes.has(ga.code))
    );
  }

  canApprove(taSubmission?: FullTaDetails | undefined): boolean {
    if (!this.currentTa() && !taSubmission) {
      return false;
    }
    if (this.isSystemAdmin()) {
      return true;
    }
    // Group members can approve if they belong to a group assigned as approver
    if (this.canApproveAsGroupMember(taSubmission)) {
      return true;
    }
    // Global scope (Finance Admin) can approve any TA
    if (this.hasPermission('ApproveTravel', [PermissionScope.Global])) {
      return true;
    }
    // Assigned scope (Line Manager, Funding Approver) — must be the designated approver
    if (!this.hasPermission('ApproveTravel', [PermissionScope.Assigned])) {
      return false;
    }
    return ((taSubmission ?? this.currentTa())!.primaryApprover?.id === this.currentUser()?.id || (taSubmission ?? this.currentTa())!.secondaryApprover?.id === this.currentUser()?.id);
  }

  canApproveAcquittal(taSubmission?: FullTaDetails | undefined): boolean {
    const ta = taSubmission ?? this.currentTa();
    if (!ta) {
      return false;
    }
    // Global scope (Finance Admin) can approve any acquittal
    if (this.hasPermission('ApproveAcquittal', [PermissionScope.Global])) {
      return true;
    }
    // Whoever holds the acquittal approval the TA currently waits on (TAStore.ApproveAcquittalCompleteSubmission).
    // The Acquittal slot is created against the primary traveller's line manager, falling back to
    // the TA's primary approver when the traveller has no manager — so on a TA approved through a
    // project or unit approver it is not the primaryApprover. A funding approver who differs from
    // it holds the SecondaryAcquittal slot, which only becomes current once the Acquittal one is
    // actioned.
    if (this.holdsPendingAcquittalApproval(ta)) {
      return true;
    }
    // Assigned scope — the designated primary approver. Only for a TA whose approval rows have not
    // been loaded, where the slots cannot be inspected.
    if (ta.approvals?.length || !this.hasPermission('ApproveAcquittal', [PermissionScope.Assigned])) {
      return false;
    }
    return ta.primaryApprover?.id === this.currentUser()?.id;
  }

  // Approving the cancellation of an actioned pre-paid TA. Mirrors the only two tests the server
  // applies (TAStore.ApproveCancellationSubmission): the holder of a pending Cancellation approval,
  // or an admin with global ManageTaSubmission who may action someone else's slot. There is no
  // primaryApprover fallback - unlike the acquittal slot, both cancellation slots are created up
  // front against the TA's primary and secondary approvers, so the rows are always inspectable.
  canApproveCancellation(taSubmission?: FullTaDetails | undefined): boolean {
    const ta = taSubmission ?? this.currentTa();
    if (!ta) {
      return false;
    }
    if (this.holdsPendingCancellationApproval(ta)) {
      return true;
    }
    return this.canApproveAnyCancellation();
  }

  // Whether the user may approve *any* TA rather than only the ones assigned to them. Held by
  // Finance Admins (Global scope) and System Admins. Unlike canApprove/canApproveAcquittal these
  // take no TA - they answer the permission question on its own, for lists of TAs whose approval
  // chain has not been loaded.
  canApproveAnyTravel(): boolean {
    return this.hasPermission('ApproveTravel', [PermissionScope.Global]);
  }

  canApproveAnyAcquittal(): boolean {
    return this.hasPermission('ApproveAcquittal', [PermissionScope.Global]);
  }

  // Cancellation approvals are the one context the server lets an admin action in place of the
  // assigned approver, and ManageTaSubmission (Global) is the permission it accepts.
  canApproveAnyCancellation(): boolean {
    return this.hasPermission('ManageTaSubmission', [PermissionScope.Global]);
  }

  // The acquittal slots (Acquittal, then SecondaryAcquittal) are always assigned to an individual,
  // never a group, so there is no group leg here — matching the server, which resolves the row by
  // ApproverId. Only the current stage counts: the funding approver waits for the manager.
  private holdsPendingAcquittalApproval(ta: FullTaDetails): boolean {
    const userId = this.currentUser()?.id;
    if (!userId) {
      return false;
    }
    const current = currentPendingApproval(ta.approvals);
    return (current?.actionContext === 'Acquittal' || current?.actionContext === 'SecondaryAcquittal')
      && current.approver?.id === userId;
  }

  // Cancellation slots are assigned to individuals (the TA's primary and, when different,
  // secondary approver), never to a group - same as the acquittal slots above.
  private holdsPendingCancellationApproval(ta: FullTaDetails): boolean {
    const userId = this.currentUser()?.id;
    if (!userId) {
      return false;
    }
    return (ta.approvals ?? []).some(approval =>
      approval.actionContext === 'Cancellation'
      && approval.status !== 'Actioned'
      && approval.approver?.id === userId);
  }

  //canRequestPrepayment same as editing a TA permissions

  canProcessPrepayment(): boolean {
    return this.hasPermission('ManagePayInAdvance', [PermissionScope.Global]);
  }

  canProcessDeduction(): boolean {
    return this.canActionComplianceApproval();
  }

  canProcessDayTripPayment(): boolean {
    return this.canActionComplianceApproval();
  }

  // Mirrors TAStore.ActionOverpaymentDeductionApprovalAsync / ActionDayTripPaymentApprovalAsync: the
  // endpoints need ViewComplianceDashboard, then the store accepts the Compliance Team group or
  // ManageTaSubmission/ManageOverpayment Global.
  private canActionComplianceApproval(): boolean {
    if (!this.hasPermission('ViewComplianceDashboard', [PermissionScope.Global])) {
      return false;
    }
    return this.currentGroups().some(group => group.code === 'complianceTeam')
      || this.hasPermission('ManageTaSubmission', [PermissionScope.Global])
      || this.hasPermission('ManageOverpayment', [PermissionScope.Global]);
  }

  // This is dependent on the current TA status rather than just checking user permission.
  canFinalise(): boolean {
    if (!this.currentTa()) {
      return false;
    }
    if (this.currentTa()?.status !== 'AcquittalApproved') {
      return false;
    }
    return this.hasPermission('FinaliseTaSubmission', [PermissionScope.Global]);
  }

  // Finalising an approved cancellation (CancelApproved -> Cancelled). Same permission and the same
  // server endpoint as canFinalise, which branches on the status
  // (AdminController.FinaliseSubmission) - only the status the TA must be in differs.
  canFinaliseCancellation(): boolean {
    if (this.currentTa()?.status !== 'CancelApproved') {
      return false;
    }
    return this.hasPermission('FinaliseTaSubmission', [PermissionScope.Global]);
  }

  // Reversing a cancellation raised in error (AdminController.RevertPrepayCancel), which takes the
  // TA back to TravelApproved or PendingAcquittal. Offered for the whole cancel flow - the same two
  // statuses the server will revert.
  //
  // Gated on FinaliseTaSubmission (Global) to allow Travel Officer and Finance Admin to use.
  canRevertCancellation(): boolean {
    const status = this.currentTa()?.status;
    if (!status || !CANCEL_FLOW_STATUSES.includes(status)) {
      return false;
    }
    return this.hasPermission('FinaliseTaSubmission', [PermissionScope.Global]);
  }

  // Cancelling a prepayment request (AdminController.CancelPrepayment). Same gate as the server:
  // FinaliseTaSubmission (Global) for Travel Officer and Finance Admin, in the statuses it accepts.
  canCancelPrepayment(): boolean {
    const status = this.currentTa()?.status;
    if (!status || !PREPAYMENT_CANCELLABLE_STATUSES.includes(status)) {
      return false;
    }
    return this.hasPermission('FinaliseTaSubmission', [PermissionScope.Global]);
  }

  // Non-assigned approval actions (approve/withhold/reject) mirror two server gates:
  // approve needs ApproveAcquittal (Global) — TAStore.ApproveSubmission;
  // reject/withhold need ManageTaSubmission (Global) — FindActionableApprovalForUser.
  // Finance Admins hold both; Travel Officers hold neither.
  canActionAnyApproval(): boolean {
    return this.hasPermission('ApproveAcquittal', [PermissionScope.Global]) &&
           this.hasPermission('ManageTaSubmission', [PermissionScope.Global]);
  }

  // Replacing the approvers on a single TA — mirrors the server's CanOverrideApprovers policy.
  // OverrideApprovers (Global) is held by Finance Admins and Travel Officers.
  canOverrideApprovers(): boolean {
    return this.hasPermission('OverrideApprovers', [PermissionScope.Global]);
  }

  ////// Navigation Permissions
  // Single source for page access: drives the nav menu (ta-layout) and minimumPermissionGuard.
  canAccessPath(path: string): boolean {
    if (path === '/approvals') {
      return this.canViewApprovalsPage();
    }
    if (path === '/manager-dashboard') {
      return this.canViewManagerDashboard();
    }
    if (path === '/finance-dashboard') {
      return this.canFinanceTas();
    }
    if (path === '/compliance-dashboard') {
      return this.canViewComplianceDashboard();
    }
    if (path.startsWith('/admin')) {
      if (path === '/admin/users') {
        return this.canManageUsers();
      }
      if (path === '/admin/locations') {
        return this.canManageLocations();
      }
      if (path === '/admin/clc-tiers') {
        return this.canManageAtoRates();
      }
      if (path === '/admin/units-projects') {
        return this.canManageProjects();
      }
      if (path === '/admin/system' || path === '/admin/auditlog') {
        return this.isSystemAdmin();
      }
      return false;
    }
    return true;
  }

  canViewApprovalsPage(): boolean {
    return this.hasGroupCodes(['systemAdmins', 'financeAdmins', 'ceo', 'generalManagers'])
      || (this.currentUser()?.isManager ?? false)
      || !!this.approverType()
  }

  canViewManagerDashboard(): boolean {
    if (this.isSystemAdmin()) {
      return true;
    }
    return this.canViewDirectReportTas() || this.canViewUnitTas();
  }

  ////// Dashboard Tab Permissions
  // The tabs are gated on the underlying relationship rather than on approverType alone, because
  // approverType is a single precedence value and the two dashboards want different cuts of it.

  // Approvals dashboard. Every approver gets the pending queue and their own history; these two are
  // the additional scope tabs. They mirror TAStore.GetSubmissionsForUnitApprover and
  // GetSubmissionsForProjectApprover, which is why they follow approverType's precedence exactly -
  // the unit leg already unions in the projects a unit approver covers.
  canViewUnitApprovals(): boolean {
    return this.approverType() === 'unit';
  }

  canViewProjectApprovals(): boolean {
    return this.approverType() === 'project';
  }

  // Manager dashboard. The two pairs are mutually exclusive: a unit manager works from the unit
  // lists, which already cover their reports' TAs by traveller hierarchy, so the direct-report pair
  // would be a narrower duplicate of what they are already looking at.
  //
  // The exclusion is driven by canViewUnitTas() rather than approverType === 'unit' so it can only
  // ever hide these tabs in favour of tabs that will actually appear - approverType is 'unit' for a
  // unit approver who manages no unit, and keying off it directly would leave that user with no
  // list at all. The admin groups are kept so the page looks the same to them as it did before the
  // unit tabs existed.
  canViewDirectReportTas(): boolean {
    if (this.canViewUnitTas()) {
      return false;
    }
    return this.hasGroupCodes(['systemAdmins', 'financeAdmins', 'ceo', 'generalManagers'])
      || (this.currentUser()?.isManager ?? false);
  }

  // Not approverType === 'unit': /user/unit-submissions resolves its scope through
  // UnitAndProjectStore.GetUnitManagerScopeAsync, which returns None when the user manages no unit.
  // A unit approver who manages nothing would get a permanently empty tab.
  //
  // NOTE the System Admin short-circuit has no server counterpart - GetUnitManagerScopeAsync
  // deliberately has no Global leg, so a System Admin is not treated as the manager of every unit.
  // These tabs therefore render for them but come back empty unless they actually manage a unit.
  canViewUnitTas(): boolean {
    return this.managedUnitCount() > 0;
  }

  canFinanceTas(): boolean {
    return this.hasPermission('ViewFinanceDashboard', [PermissionScope.Global]);
  }

  canViewComplianceDashboard(): boolean {
    return this.hasPermission('ViewComplianceDashboard', [PermissionScope.Global]);
  }

  /** The per-traveller Finance/Compliance payment split is only for admin, Finance and Compliance. */
  canViewDayTripBreakdown(): boolean {
    return this.isSystemAdmin() || this.canFinanceTas() || this.canViewComplianceDashboard();
  }

  canManageLocations(): boolean {
    return this.hasPermission('ManageLocations', [PermissionScope.Global]);
  }

  canManageUsers(): boolean {
    return this.hasPermission('ManageUsers', [PermissionScope.Global]);
  }

  canManageAtoRates(): boolean {
    return this.hasPermission('ManageAtoRates', [PermissionScope.Global]);
  }

  canManageProjects(): boolean {
    return this.hasPermission('ManageUnitsAndProjects', [PermissionScope.Global]);
  }

  canCreateDelegateTa(): boolean {
    return this.hasPermission('ManageDelegateTa', [PermissionScope.Global, PermissionScope.Assigned]);
  }

  // This doesn't check if the user is the exact delegate for the TA, but if they have the right requirements to be the delegate.
  // Finance admins don't have to be the assigned person but they *can* be the delegate.
  // For non-finance admins we check the delegate ID is a match and the user has the correct permissions.
  canBeDelegateForTa(taSubmission?: FullTaDetails | BasicTaDetails): boolean{
    if (!!taSubmission) {
      if (this.isSystemAdmin() || this.hasPermission('ManageDelegateTa', [PermissionScope.Global])){
        return true;
      }

      return taSubmission!.delegate != null &&
      this.hasPermission('ManageDelegateTa', [PermissionScope.Assigned])
      && taSubmission!.delegate!.id === this.currentUser()?.id;
    }
    return false;
  }

  // Was this TA raised by a booking officer on someone else's behalf? Nothing to do with who is looking at it.
  isDelegateTa(taSubmission?: FullTaDetails | BasicTaDetails): boolean {
    return (taSubmission ?? this.currentTa())?.delegate != null;
  }

  // Mirrors the server's PermissionService.IsDelegateFor: the session user IS the booking officer on this TA.
  // Deliberately has no Global/sysAdmin short-circuit (unlike canBeDelegateForTa) so we never enable an action
  // the server will refuse - the server has no Global short-circuit either.
  isDelegateForTa(taSubmission?: FullTaDetails | BasicTaDetails): boolean {
    const ta = taSubmission ?? this.currentTa();
    const userId = this.currentUser()?.id;
    return !!userId && ta?.delegate?.id != null && ta.delegate.id === userId;
  }

}
